import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { useApp } from './AppContext';
import { formatTime } from '../utils/dateUtils';
import { ConfirmationDialog } from './ConfirmationDialog';

export function CheckOutList() {
  const { 
    staffMembers, 
    setStaffMembers, 
    setCurrentScreen, 
    supervisorTasks, 
    updateTaskCompletion,
    navigateToMyEventWithContext,
    navigationContext
  } = useApp();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Initialize staff members if not already set
    if (staffMembers.length === 0) {
      const initialStaff = [
        {
          id: '1',
          name: 'Staff 1',
          idCard: 'DE123456789',
          checkedIn: true,
          checkInTime: '09:06 AM',
          checkedOut: true,
          checkOutTime: '09:06 AM'
        },
        {
          id: '2',
          name: 'Staff 2',
          idCard: 'DE123456790',
          checkedIn: true,
          checkInTime: '09:04 AM',
          checkedOut: true,
          checkOutTime: '09:00 AM'
        },
        {
          id: '3',
          name: 'Staff 3',
          idCard: 'DE123456791',
          checkedIn: true,
          checkInTime: '09:02 AM',
          checkedOut: false
        },
        {
          id: '4',
          name: 'Staff 4',
          idCard: 'DE123453453',
          checkedIn: true,
          checkInTime: '09:01 AM',
          checkedOut: false
        }
      ];
      setStaffMembers(initialStaff);
    }
  }, [staffMembers, setStaffMembers]);

  // Check if all staff are checked out and update task completion
  useEffect(() => {
    if (staffMembers.length > 0) {
      const allCheckedOut = staffMembers.every(staff => staff.checkedOut);
      const checkOutTask = supervisorTasks.find(task => task.supervisorType === 'check-out');
      
      if (checkOutTask && checkOutTask.completed !== allCheckedOut) {
        updateTaskCompletion(checkOutTask.id, allCheckedOut);
      }
    }
  }, [staffMembers, supervisorTasks, updateTaskCompletion]);

  const handleBack = () => {
    // Always return to task list from check-out list
    setCurrentScreen('task-list');
  };

  const handleCheckOutClick = (staff: any) => {
    setSelectedStaff(staff);
    setDialogOpen(true);
  };

  const handleConfirmCheckOut = async () => {
    if (!selectedStaff) return;

    setIsProcessing(true);
    
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 800));

    // Create a time string in HH:MM format for formatTime function
    const now = new Date();
    const timeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:00`;
    const currentTime = formatTime(timeString);

    setStaffMembers(prev => prev.map(staff => 
      staff.id === selectedStaff.id 
        ? { ...staff, checkedOut: true, checkOutTime: currentTime }
        : staff
    ));

    setIsProcessing(false);
    setDialogOpen(false);
    setSelectedStaff(null);
  };

  const handleCloseDialog = () => {
    if (!isProcessing) {
      setDialogOpen(false);
      setSelectedStaff(null);
    }
  };

  const pendingStaff = staffMembers.filter(staff => staff.checkedIn && !staff.checkedOut);
  const checkedOutStaff = staffMembers.filter(staff => staff.checkedOut);
  const progressPercentage = staffMembers.length > 0 
    ? (checkedOutStaff.length / staffMembers.length) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col md:flex-row">
      {/* Content container with responsive constraints */}
      <div className="flex-1 flex justify-center md:ml-20">
        <div className="w-full max-w-[calc(100vw-32px)] md:max-w-[500px] px-4 py-6">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={handleBack}
              className="w-12 h-12 rounded-full bg-[#f3f4f6] flex items-center justify-center hover:bg-[#e5e7eb] transition-colors"
            >
              <ArrowLeft size={20} className="text-[#6b7280]" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="w-full bg-[#e5e7eb] rounded-sm h-1">
              <div 
                className="bg-[#0f2027] h-1 rounded-sm transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Title */}
          <div className="mb-6">
            <h1 style={{
              fontFamily: 'Figtree',
              fontWeight: 700,
              fontSize: '28px',
              color: '#1f2937',
              margin: 0
            }}>
              Check-out List
            </h1>
          </div>

          {/* Staff List */}
          <div className="space-y-4">
            {/* Pending Check-out Items */}
            {pendingStaff.map((staff) => (
              <div
                key={staff.id}
                className="bg-white rounded-2xl p-5 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 style={{
                      fontFamily: 'Figtree',
                      fontWeight: 600,
                      fontSize: '18px',
                      color: '#1f2937',
                      margin: 0
                    }}>
                      {staff.name}
                    </h3>
                    <p style={{
                      fontFamily: 'Figtree',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '4px 0 0 0'
                    }}>
                      ID Card {staff.idCard}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCheckOutClick(staff)}
                    className="h-10 px-5 bg-[#0f2027] text-white font-semibold rounded-full hover:bg-[#1e3a3a] transition-colors duration-200"
                    style={{
                      fontFamily: 'Figtree',
                      fontWeight: 600,
                      fontSize: '14px'
                    }}
                  >
                    Check-out
                  </button>
                </div>
              </div>
            ))}

            {/* Completed Check-out Items */}
            {checkedOutStaff.map((staff) => (
              <div
                key={staff.id}
                className="bg-white rounded-2xl p-5 shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{
                      fontFamily: 'Figtree',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#9ca3af',
                      margin: '0 0 4px 0'
                    }}>
                      Check-out time : {staff.checkOutTime}
                    </p>
                    <h3 style={{
                      fontFamily: 'Figtree',
                      fontWeight: 600,
                      fontSize: '18px',
                      color: '#9ca3af',
                      margin: 0
                    }}>
                      {staff.name}
                    </h3>
                    <p style={{
                      fontFamily: 'Figtree',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#9ca3af',
                      margin: '4px 0 0 0'
                    }}>
                      ID Card number : {staff.idCard}
                    </p>
                  </div>
                  <div className="flex items-center justify-center">
                    <Check size={24} className="text-[#22c55e]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Universal Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={dialogOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmCheckOut}
        title="Check-out bestätigen"
        message="Möchten Sie [Staff Name] wirklich auschecken?"
        staffName={selectedStaff?.name || ''}
        idCard={selectedStaff?.idCard || ''}
        confirmText="Check-out bestätigen"
        isLoading={isProcessing}
      />
    </div>
  );
}